import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Badge, Empty, statusTone } from "@/components/admin/Ui";
import { PaperPlaneTilt, EnvelopeSimple, Sparkle } from "@phosphor-icons/react";

export default function Outreach() {
  const [outreach, setOutreach] = useState([]);
  const [leads, setLeads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedLead, setSelectedLead] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [o, l] = await Promise.all([
      api.get("/outreach"),
      api.get("/leads"),
    ]);
    setOutreach(o.data.outreach || []);
    setLeads(l.data.leads || []);
  };

  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!selectedLead) { toast.error("Pick a lead"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/outreach/generate", { lead_id: selectedLead });
      toast.success("Draft created");
      await load();
      setSelected(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Draft failed");
    } finally { setBusy(false); }
  };

  const send = async (id) => {
    setBusy(true);
    try {
      await api.post("/outreach/send", { id });
      toast.success("Marked as sent (MOCKED — no real email delivery)");
      await load();
      if (selected?.id === id) setSelected((s) => ({ ...s, status: "sent" }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-outreach-page">
      <PageHeader title="Outreach" subtitle="Agent 3 · GPT-5-mini · MOCKED send" />
      {busy && (
        <div className="mx-8 mt-6 bg-white border border-black/10 p-3 flex items-center gap-3 text-sm text-navy/70" data-testid="outreach-busy-loading">
          <div className="w-3 h-3 rounded-full bg-ember animate-pulse" />
          Working… AI drafting can take 10–20 seconds.
        </div>
      )}
      <div className="p-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-4 space-y-4">
          <div className="bg-white border border-black/10 p-5">
            <div className="overline mb-3">Draft new outreach</div>
            <select
              data-testid="outreach-lead-select"
              value={selectedLead}
              onChange={(e) => setSelectedLead(e.target.value)}
              className="w-full border border-black/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy"
            >
              <option value="">Select a lead…</option>
              {leads.map((l) => {
                const label = `${l.company_name} · ${l.country} (${l.score})`;
                return <option key={l.id} value={l.id}>{label}</option>;
              })}
            </select>
            <button
              data-testid="outreach-generate-button"
              onClick={generate}
              disabled={busy || !selectedLead}
              className="btn-primary w-full justify-center mt-3"
            >
              <Sparkle size={14} /> {busy ? "Drafting…" : "Draft email"}
            </button>
          </div>

          <div className="bg-white border border-black/10">
            <div className="px-5 py-3 border-b border-black/10 flex items-center gap-2">
              <EnvelopeSimple size={16} weight="duotone" className="text-ember" />
              <span className="font-semibold text-sm text-navy">All drafts</span>
            </div>
            <ul className="divide-y divide-black/10 max-h-[500px] overflow-y-auto">
              {outreach.map((o) => (
                <li key={o.id}
                  onClick={() => setSelected(o)}
                  data-testid={`outreach-item-${o.id}`}
                  className={`px-5 py-3 cursor-pointer hover:bg-navy/5 text-sm ${selected?.id === o.id ? "bg-navy/5 border-l-2 border-ember" : ""}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-navy truncate max-w-[180px]">{o.subject || "(no subject)"}</div>
                    <Badge tone={statusTone(o.status)}>{o.status}</Badge>
                  </div>
                  <div className="text-xs text-navy/60 mt-1 truncate">{o.to_email}</div>
                </li>
              ))}
              {outreach.length === 0 && <li className="p-5 text-xs text-navy/50">No drafts yet.</li>}
            </ul>
          </div>
        </aside>

        <section className="lg:col-span-8">
          {!selected ? (
            <Empty label="Select or create a draft to preview." />
          ) : (
            <div className="bg-white border border-black/10" data-testid="outreach-preview">
              <div className="px-6 py-4 border-b border-black/10 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="overline">To</div>
                  <div className="text-sm text-navy font-medium truncate">{selected.to_email || "—"}</div>
                </div>
                <Badge tone={statusTone(selected.status)}>{selected.status}</Badge>
              </div>
              <div className="px-6 py-4 border-b border-black/10">
                <div className="overline mb-1">Subject</div>
                <div className="font-display font-bold text-lg text-navy">{selected.subject}</div>
              </div>
              <div className="px-6 py-5 text-sm text-navy whitespace-pre-wrap leading-relaxed" data-testid="outreach-body">{selected.body}</div>
              {selected.personalization_notes && (
                <div className="px-6 py-4 border-t border-black/10 text-xs text-navy/60">
                  <span className="font-semibold text-navy">Personalization notes: </span>{selected.personalization_notes}
                </div>
              )}
              <div className="px-6 py-4 border-t border-black/10 flex items-center justify-end gap-3">
                {selected.status !== "sent" && (
                  <button
                    data-testid="outreach-send-button"
                    disabled={busy}
                    onClick={() => send(selected.id)}
                    className="btn-primary bg-ember hover:bg-ember-600"
                  >
                    <PaperPlaneTilt size={14} /> Send (mock)
                  </button>
                )}
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
